import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Filter, Download, Phone, MessageCircle, Eye } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "../AdminLayout";
import { adminAxios, API, getToken, formatErr } from "../api";
import { StatusPill } from "./AdminDashboard";
import { useCatalog } from "../../contexts/CatalogContext";

const STATUS_OPTIONS = ["", "confirmed", "assigned", "on_the_way", "completed", "cancelled"];

export default function AdminBookings() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");
  const [page, setPage] = useState(Number(params.get("page") || 1));
  const [pageSize, setPageSize] = useState(Number(params.get("pageSize") || 25));

  const filters = useMemo(() => ({
    status: params.get("status") || "",
    category: params.get("category") || "",
    city: params.get("city") || "",
    segment: params.get("segment") || "",
    q: params.get("q") || "",
  }), [params]);

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && qs.set(k, v));
      qs.set("limit", String(pageSize));
      qs.set("skip", String((page - 1) * pageSize));
      const { data: d } = await adminAxios.get(`/bookings?${qs.toString()}`);
      setData(d);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters, page, pageSize]);

  const { categories: CATEGORIES, CITIES } = useCatalog();

  const exportCsv = () => {
    const url = `${API}/admin/bookings/export.csv`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "repairro-bookings.csv";
        a.click();
      });
  };

  return (
    <AdminLayout title="Bookings">
      {/* Filters bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4" data-testid="bookings-filters">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-semibold text-slate-600">Search</label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && updateFilter("q", q)}
                placeholder="Booking ID, name or phone"
                data-testid="bookings-search"
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-[#1A3C6E] outline-none"
              />
            </div>
          </div>
          <Select label="Status" value={filters.status} onChange={(v) => updateFilter("status", v)} testid="filter-status">
            <option value="">All</option>
            {STATUS_OPTIONS.filter(Boolean).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </Select>
          <Select label="Category" value={filters.category} onChange={(v) => updateFilter("category", v)} testid="filter-category">
            <option value="">All</option>
            {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.short}</option>)}
          </Select>
          <Select label="City" value={filters.city} onChange={(v) => updateFilter("city", v)} testid="filter-city">
            <option value="">All</option>
            {CITIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Select label="Segment" value={filters.segment} onChange={(v) => updateFilter("segment", v)} testid="filter-segment">
            <option value="">All</option>
            <option value="home">Home</option>
            <option value="shop">Shop/Office</option>
            <option value="professional">Professional</option>
          </Select>
          <button
            onClick={() => { setQ(""); setParams(new URLSearchParams()); }}
            className="text-xs font-semibold text-slate-500 px-3 py-2"
            data-testid="filters-clear"
          >
            Clear
          </button>
          <button
            onClick={exportCsv}
            data-testid="export-csv"
            className="ml-auto inline-flex items-center gap-2 bg-[#1A3C6E] hover:bg-[#132D53] text-white text-sm font-semibold px-4 py-2 rounded-lg"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 text-sm text-slate-500">
          {loading ? "Loading…" : (
            <>
              Showing <span className="font-semibold text-slate-900">{Math.min((page-1)*pageSize+1, data.total)}</span> - <span className="font-semibold text-slate-900">{Math.min(page*pageSize, data.total)}</span> of {data.total}
            </>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bookings-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="px-5 py-2.5">ID</th>
                <th className="px-5 py-2.5">Customer</th>
                <th className="px-5 py-2.5">Service</th>
                <th className="px-5 py-2.5">Brand</th>
                <th className="px-5 py-2.5">City</th>
                <th className="px-5 py-2.5">Date / Slot</th>
                <th className="px-5 py-2.5">Status</th>
                <th className="px-5 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={8} className="px-5 py-10 text-center text-slate-400">No bookings match these filters.</td></tr>
              )}
              {data.items.map((b) => (
                <tr key={b.booking_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`booking-row-${b.booking_id}`}>
                  <td className="px-5 py-3">
                    <Link to={`/admin/bookings/${b.booking_id}`} className="font-semibold text-[#1A3C6E] hover:underline">
                      {b.booking_id}
                    </Link>
                  </td>
                  <td className="px-5 py-3">
                    <div className="font-medium text-slate-900">{b.name}</div>
                    <div className="text-xs text-slate-500">{b.phone}</div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-slate-800">{b.service_name}</div>
                    <div className="text-xs text-slate-500 capitalize">{b.category}</div>
                  </td>
                  <td className="px-5 py-3">{b.brand}</td>
                  <td className="px-5 py-3 capitalize">{b.city}</td>
                  <td className="px-5 py-3">
                    <div>{b.date}</div>
                    <div className="text-xs text-slate-500 capitalize">{b.slot}</div>
                  </td>
                  <td className="px-5 py-3"><StatusPill status={b.status} /></td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-1">
                      <Link to={`/admin/bookings/${b.booking_id}`} className="p-1.5 rounded-md hover:bg-slate-200" title="View" data-testid={`row-view-${b.booking_id}`}>
                        <Eye className="w-4 h-4 text-slate-600" />
                      </Link>
                      <a href={`tel:${b.phone}`} className="p-1.5 rounded-md hover:bg-slate-200" title="Call">
                        <Phone className="w-4 h-4 text-slate-600" />
                      </a>
                      <a href={`https://wa.me/91${b.phone}`} target="_blank" rel="noreferrer" className="p-1.5 rounded-md hover:bg-green-100" title="WhatsApp">
                        <MessageCircle className="w-4 h-4 text-[#22C55E]" />
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Pagination */}
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p-1))} className="px-3 py-1 rounded-md border">Previous</button>
          <button disabled={(page*pageSize) >= data.total} onClick={() => setPage((p) => p+1)} className="px-3 py-1 rounded-md border">Next</button>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-500">Per page</label>
          <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="px-2 py-1 border rounded">
            {[10,25,50,100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>
    </AdminLayout>
  );
}

const Select = ({ label, value, onChange, children, testid }) => (
  <div>
    <label className="text-xs font-semibold text-slate-600">{label}</label>
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="block mt-1 px-3 py-2 text-sm rounded-lg border border-slate-200 focus:border-[#1A3C6E] outline-none bg-white"
    >
      {children}
    </select>
  </div>
);
